/**
 * Carousel Component
 * @module patterns/components/accordion
 */

import { App } from '~/app';

export default (app: App) => {

    app.logger.debug('[Stockland FE] Initialise Stockland Accordion Component');

    const listeners: Array<[Element | Window | Document, string, EventListenerOrEventListenerObject]> = [];

    app.afterMount
        .then(() => {
            mountAccordions(listeners);
        })
        .catch((error) => {
            throw new Error('[Stockland FE] Unable to register Stockland Accordion component', error);
        });

    return function destroyAccordion() {
        unMountAccordions(listeners);

        app.logger.debug('[DXA] Accordion component destroyed');
    };
}

// Extracted method for logic reuse within other component
export function mountAccordions(
    listeners: Array<[Element | Window | Document, string, EventListenerOrEventListenerObject]>,
    root: HTMLElement | null = null) {

    let rootNode: Document | HTMLElement = document;
    if (root)
        rootNode = root;


    const accordions = rootNode.querySelectorAll<HTMLElement>('.accordion');
    for (const accordion of accordions)
    {
        const accordionItems = accordion.querySelectorAll(".accordion-item");
        for (const accordionItem of accordionItems)
        {
            if (accordionItem)
            {
                const accordionHeader = accordionItem.querySelector<HTMLElement>(".accordion-header");
                const accordionBody = accordionItem.querySelector<HTMLElement>(".accordion-body");

                if (accordionHeader)
                {
                    const iconContainer = accordionHeader.querySelector<SVGSVGElement>(".icons");

                    const accordionHeaderClickFunction = function() {
                        if (iconContainer && accordionBody)
                        {
                            accordionHeader.classList.toggle("accordion-header-active");
                            accordionItem.classList.toggle("accordion-item-before");

                            // handles the accordion icon changes for different variants
                            const arrow = iconContainer.querySelector<SVGSVGElement>('.arrow')
                            const tertiaryArrow = iconContainer.querySelector<SVGSVGElement>('.tertiary-arrow')
                            const plusMinus = iconContainer.querySelectorAll<SVGSVGElement>('.plus-minus')

                            if (arrow){
                                arrow.classList.toggle("rotate-180");
                            }

                            if (tertiaryArrow){
                                tertiaryArrow.classList.toggle("rotate-180")
                            }

                            if (plusMinus){
                                for (const icon of plusMinus) {
                                    icon.classList.toggle("hidden");
                                }
                            }

                            /* This allows the accordion body to gain the transition effect when
							expanding/collapsing.
								Using Tailwind for styles was unsuccessful due to needing to calculate the max-height
								on the fly and using arbitrary values did not work so inline styles were required. */
                            if (accordionBody.style.maxHeight == "0px")
                            {
                                const accordionText = accordionBody.querySelector<HTMLElement>(".accordion-text");

                                if (accordionText)
                                {
                                    const divHeight = accordionText.scrollHeight;
                                    accordionBody.style.maxHeight = `${divHeight}px`;
                                }
                            }
                            else
                            {
                                accordionBody.style.maxHeight = "0px";
                            }
                        }
                    }

                    accordionHeader.addEventListener("click", accordionHeaderClickFunction);
                    listeners.push([accordionHeader, "click", accordionHeaderClickFunction]);

                    const adjustAccordionMaxHeight = () => {
                        if (accordionBody) {
                            // eslint-disable-next-line unicorn/no-lonely-if
                            if (accordionBody.style.maxHeight !== "0px") {
                                const accordionText = accordionBody.querySelector<HTMLElement>(".accordion-text");

                                if (accordionText) {
                                    const divHeight = accordionText.scrollHeight;
                                    accordionBody.style.maxHeight = `${divHeight}px`;
                                }
                            }
                        }
                    }

                    window.addEventListener("resize", adjustAccordionMaxHeight);
                    listeners.push([window, "resize", adjustAccordionMaxHeight]);
                }
            }
        }
    }
}

export function unMountAccordions(listeners: Array<[Element | Window | Document, string, EventListenerOrEventListenerObject]>) {
    for (const [element, event, listener] of listeners) {
        element.removeEventListener(event, listener);
    }
}
